import type { ReactNode } from 'react'
import { useState } from 'react'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { NextSeo } from 'next-seo'

import { useIsomorphicLayoutEffect } from 'react-use'

import { lang } from '@lib/constants'
import { getRouteSubNavItems } from '@lib/utils/account-nav-items'

import AccountInfoBar from '../AccountInfoBar'
import AccountVerticalNav from './AccountVerticalNav'
import SubNav from './SubNav'
import WelcomeBar from './WelcomeBar'

type AccountLayoutProps = {
	children: ReactNode
}

export default function AccountLayout({ children }: AccountLayoutProps) {
	const router = useRouter()

	const [subNavTitle, setSubNavTitle] = useState<string | null>(null)
	const [showNav, setShowNav] = useState(false)

	const { indexNavPage, verticalNavItems } = getRouteSubNavItems(router.pathname) || {}

	// Set subnav title for mobile back button
	useIsomorphicLayoutEffect(() => {
		if (verticalNavItems && indexNavPage && router.pathname !== indexNavPage) {
			const newSubNavTitle = verticalNavItems?.find(
				(routeObj) => routeObj?.url === router.pathname
			)?.label

			if (newSubNavTitle) {
				setSubNavTitle(newSubNavTitle)
			}
		} else {
			setSubNavTitle(null)
		}
	}, [verticalNavItems, indexNavPage, router.pathname, setSubNavTitle])

	const isProfileOrCustomize =
		router.pathname === '/profile/subscriptions' ||
		router.pathname.startsWith('/profile/subscriptions') ||
		router.pathname === '/profile/customize' ||
		router.pathname === '/profile/customization'

	const containerClass = !isProfileOrCustomize
		? 'grid grid-cols-1 lg:grid-cols-3 min-h-[300px] content-start overflow-y-visible container relative'
		: 'grid-cols-1 md:grid-cols-1 lg:grid-cols-1 min-h-[80vh]'

	return (
		<div className="bg-bbx-pearl-lightest border-b border-gray-200">
			<NextSeo noindex nofollow />
			<SubNav />

			{lang !== 'en-us' && lang !== 'en-gb' && <AccountInfoBar />}

			<div className={` ${containerClass}`}>
				{subNavTitle && indexNavPage && (
					<div className="relative flex items-center justify-center w-full py-8 border-b border-gray-200 lg:hidden h-min">
						{router.pathname.includes('/profile/account/') ? (
							<button
								type="button"
								className={`absolute left-0 p-1 ${showNav === true && 'hidden'}`}
								onClick={() => setShowNav(!showNav)}
							>
								<i className="fa-light fa-chevron-left w-2.5 text-gray-500" />
							</button>
						) : (
							<Link legacyBehavior href={indexNavPage}>
								<a className="absolute left-0 p-1">
									<i className="fa-light fa-chevron-left w-2.5 text-gray-500" />
								</a>
							</Link>
						)}

						<h2 className="font-serif text-xl">{subNavTitle}</h2>
					</div>
				)}

				{verticalNavItems && (
					<AccountVerticalNav
						isIndexNavPage={router.pathname === indexNavPage}
						navItems={verticalNavItems}
						showNav={showNav} // Pass showNav state
						setShowNav={setShowNav} // Pass the function to update showNav state
					/>
				)}

				{children}
			</div>
		</div>
	)
}
